/* eslint-disable no-magic-numbers */
import { fileUploadStatusSchema } from '@/models/fileSchema';
import { axiosInstanceNewAPI } from '@/services/api/utils/axiosInstance';
import { FileUploadStatus } from '@/types/models';
import { validateDataUsingZodSchema } from '@/utils/validateDataUsingZodSchema';
import { apiPath } from '../apiPath';

const FIRST_CHUNK_NUMBER = 1;
const CHUNK_SIZE = 65535 * 8;

const toBytes = async (content: string | File): Promise<Uint8Array> =>
  typeof content === 'string'
    ? new TextEncoder().encode(content)
    : new Uint8Array(await content.arrayBuffer());

const parseFileUploadStatus = (data: FileUploadStatus): FileUploadStatus => {
  const isDataValid = validateDataUsingZodSchema(data, fileUploadStatusSchema);
  if (!isDataValid) {
    throw new Error('Invalid response from server');
  }
  return data;
};

type InitiateFileUploadArgs = {
  filename: string;
  totalChunks?: number;
};

const initiateFileUpload = async ({
  filename,
  totalChunks,
}: InitiateFileUploadArgs): Promise<FileUploadStatus> => {
  const response = await axiosInstanceNewAPI.post<FileUploadStatus>(apiPath.initiateFileUpload(), {
    originalFileName: filename,
    ...(totalChunks === undefined ? {} : { totalChunks }),
  });

  return parseFileUploadStatus(response.data);
};

type UploadFileChunkArgs = {
  fileId: number;
  chunkNumber: number;
  chunk: Uint8Array;
};

const uploadFileChunk = async ({
  fileId,
  chunkNumber,
  chunk,
}: UploadFileChunkArgs): Promise<void> => {
  await axiosInstanceNewAPI.put(apiPath.uploadFileChunk(fileId, chunkNumber), chunk, {
    headers: {
      'Content-Type': 'application/octet-stream',
    },
  });
};

const completeFileUpload = async (fileId: number): Promise<FileUploadStatus> => {
  const response = await axiosInstanceNewAPI.post<FileUploadStatus>(
    apiPath.completeFileUpload(fileId),
  );

  return parseFileUploadStatus(response.data);
};

type UploadFileArgs = {
  filename: string;
  content: string | File;
};

const uploadFile = async ({ filename, content }: UploadFileArgs): Promise<FileUploadStatus> => {
  const data = await toBytes(content);
  const totalChunks = Math.ceil(data.length / CHUNK_SIZE);

  const { id: fileId } = await initiateFileUpload({ filename, totalChunks });

  let chunkNumber = FIRST_CHUNK_NUMBER;
  for (let offset = 0; offset < data.length; offset += CHUNK_SIZE) {
    const chunk = data.slice(offset, offset + CHUNK_SIZE);
    // eslint-disable-next-line no-await-in-loop
    await uploadFileChunk({ fileId, chunkNumber, chunk });
    chunkNumber += 1;
  }

  return completeFileUpload(fileId);
};

type CreateFileArgs = {
  filename: string;
  content: string | File;
};

type CreatedFile = {
  id: number;
  filename: string;
};

export const createFile = async ({ filename, content }: CreateFileArgs): Promise<CreatedFile> => {
  const { id } = await uploadFile({ filename, content });

  return { id, filename };
};
